// Rechts neben den Summen im Rechnungsfenster:
//   offen (gestellt)  → GiroCode zum Abscannen, oder warum es keinen gibt
//   bezahlt           → roter Stempel BEZAHLT mit Datum
// Derselbe Platz in beiden Fällen, damit man auf einen Blick sieht, woran man ist —
// und nichts über Positionen oder Knöpfen liegt.
import { useEffect, useState } from 'react';
import { api } from '../../../gemeinsam/lib/api';
import { datum } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import type { Rechnung } from '../../../gemeinsam/lib/typen';

type Zahlcode = { svg: string } | { svg: null; grund: string | null };

/** Letzter Zahlungstag — bei alten, von Hand als bezahlt markierten Rechnungen bezahltAm. */
function bezahltAm(r: Rechnung) {
  const tage = (r.zahlungen ?? []).map(z => z.datum).filter(Boolean).sort();
  return tage.at(-1) ?? r.bezahltAm ?? null;
}

export function BezahltStempel({ r }: { r: Rechnung }) {
  const { t } = useSprache();
  const am = bezahltAm(r);
  return (
    <div className="inline-flex -rotate-6 select-none flex-col items-center rounded-lg border-[3px] border-red-600 px-4 py-1.5 text-red-600 dark:border-red-500 dark:text-red-500"
         aria-label={t('rechnungen.stempelBezahlt')}>
      <span className="text-2xl font-black uppercase leading-none tracking-[0.12em]">{t('rechnungen.stempelBezahlt')}</span>
      {am && <span className="mt-1 text-[11px] font-semibold">{t('rechnungen.stempelAm').replace('{datum}', datum(am))}</span>}
    </div>
  );
}

export function GiroCode({ r }: { r: Rechnung }) {
  const { t } = useSprache();
  const [code, setCode] = useState<Zahlcode | null>(null);
  // Neu holen, sobald sich der offene Betrag ändert (Teilzahlung, Gutschrift).
  const stand = `${r.id}|${r.status}|${r.brutto}|${(r.zahlungen ?? []).length}`;

  useEffect(() => {
    let aktiv = true;
    setCode(null);
    api<Zahlcode>(`/invoices/${r.id}/zahlcode`)
      .then(c => { if (aktiv) setCode(c); })
      .catch(() => { if (aktiv) setCode({ svg: null, grund: null }); });
    return () => { aktiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand]);

  if (!code) return <div className="h-40 w-40 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />;
  if (code.svg === null) {
    return code.grund ? (
      <p className="max-w-56 rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
        <span className="block font-medium text-slate-700 dark:text-slate-300">{t('rechnungen.keinGirocode')}</span>
        {code.grund}
      </p>
    ) : null;
  }
  return (
    <figure className="flex flex-col items-center gap-1.5">
      {/* SVG kommt aus unserem eigenen QR-Erzeuger (nur Pfade, keine Fremdinhalte). */}
      <div className="rounded-lg bg-white p-1.5 ring-1 ring-slate-200 [&>svg]:h-40 [&>svg]:w-40 dark:ring-slate-700"
           dangerouslySetInnerHTML={{ __html: code.svg }} />
      <figcaption className="max-w-48 text-center text-[11px] leading-snug text-slate-500 dark:text-slate-400">
        {t('rechnungen.girocodeText')}
      </figcaption>
    </figure>
  );
}
